import { useEffect, useState } from 'react'
import { DatePicker } from 'react-datepicker'

import { IconButton } from '@mui/material'

import { DateRangeCalendarContainer } from './DateRangeCalendarContainer'
import { DateRangeField } from './DateRangeField'
import { DateRangeHeader } from './DateRangeHeader'

type ControlledProps = {
  startDate: Date | null
  endDate: Date | null
}

type UncontrolledProps = {
  startDate?: never
  endDate?: never
}

type Props = {
  label: string
  onChange: (dates: { startDate: Date | null; endDate: Date | null }) => void
  clearAfterSelect?: boolean
  format?: string
  error?: boolean
  width?: number | string
  startDate?: Date | null
  endDate?: Date | null
  defaultValues?: {
    startDate?: Date
    endDate?: Date
  }
} & (ControlledProps | UncontrolledProps)

export const DateRangePicker = ({
  label,
  startDate,
  endDate,
  clearAfterSelect,
  format,
  error,
  width,
  defaultValues,
  onChange
}: Props) => {
  const isControlled = startDate !== undefined && endDate !== undefined
  const [internalStartDate, setInternalStartDate] = useState<Date | null>(
    defaultValues?.startDate || null
  )
  const [internalEndDate, setInternalEndDate] = useState<Date | null>(
    defaultValues?.endDate || null
  )

  const startDateValue = isControlled ? startDate : internalStartDate
  const endDateValue = isControlled ? endDate : internalEndDate

  useEffect(() => {
    if (!isControlled) return
    setInternalStartDate(startDate)
  }, [startDate, isControlled])

  useEffect(() => {
    if (!isControlled) return
    setInternalEndDate(endDate)
  }, [endDate, isControlled])

  const handleClearAfterSelect = () => {
    setInternalStartDate(null)
    setInternalEndDate(null)
  }

  const handleDatesChange = (dates: [Date | null, Date | null]) => {
    const [startDate, endDate] = dates

    if (!isControlled) {
      setInternalStartDate(startDate)
      setInternalEndDate(endDate)
    }

    if (!startDate || !endDate) return

    startDate.setHours(0, 0, 0, 0)
    endDate.setHours(23, 59, 59, 999)

    onChange({
      startDate,
      endDate
    })
  }

  return (
    <DatePicker
      renderCustomHeader={DateRangeHeader}
      customInput={
        <DateRangeField
          startDate={internalStartDate}
          endDate={internalEndDate}
          format={format}
          label={label}
          error={error}
          width={width}
        />
      }
      calendarContainer={DateRangeCalendarContainer}
      showPopperArrow={false}
      renderDayContents={(dayNumber) => (
        <IconButton
          sx={{
            width: '40px',
            height: '40px'
          }}
          size="small"
        >
          {dayNumber}
        </IconButton>
      )}
      portalId="root-portal"
      popperPlacement="bottom-start"
      monthsShown={2}
      calendarStartDay={1}
      onChange={handleDatesChange}
      selectsRange
      shouldCloseOnSelect
      onCalendarClose={
        clearAfterSelect && startDateValue && endDateValue ? handleClearAfterSelect : () => {}
      }
      selected={startDateValue}
      startDate={startDateValue}
      endDate={endDateValue}
    />
  )
}
